import React, { useState, useRef, useEffect } from "react";
import DoctorMultiSelect from "../DoctorMultiSelect";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { format, startOfWeek, endOfWeek } from "date-fns";
import TeamsDatePicker from "../TeamsDatePicker";
import { usePermission } from "../../hooks/use-permission";
import { Button } from "../ui/button";

const viewOptions = [
  { value: "day", label: "Day" },
  { value: "month", label: "Month" },
  { value: "week", label: "Week" },
];

const CustomToolbar = ({
  date,
  onNavigate,
  onView,
  view,
  selectedDoctors,
  onDoctorUpdate,
  onAddAppointment,
  onAddBulkAppointment,
}) => {
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const canAddAppointment = usePermission("appointments.add", "write");
  const canSelectProviders = usePermission("appointments.select_providers", "read");

  const viewMenuRef = useRef(null);
  const datePickerRef = useRef(null);
  const addMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) {
        setViewMenuOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setDatePickerOpen(false);
      }
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setShowAddMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isPastDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selected = new Date(date);
    selected.setHours(0, 0, 0, 0);

    return selected < today;
  };

  const formatDateLabel = () => {
    if (view === "day") return format(date, "EEEE MMM dd");

    if (view === "week") {
      const start = startOfWeek(date, { weekStartsOn: 0 });
      const end = endOfWeek(date, { weekStartsOn: 0 });
      return `${format(start, "MMMM d")} – ${format(end, "MMMM d, yyyy")}`;
    }

    return format(date, "MMMM yyyy");
  };

  const label = formatDateLabel();

  const handleViewSelect = (value) => {
    setViewMenuOpen(false);
    onView(value);
  };

  const handleSelectDoctors = (ids, list, options = {}) => {
    onDoctorUpdate(ids, list);
    if (options.closeDropdown) setDropdownOpen(false);
  };

  const currentViewLabel =
    viewOptions.find((opt) => opt.value === view)?.label || "Week";

  return (
    <div className="w-full border-b border-gray-200 bg-white px-4 py-3 flex items-center justify-between gap-4">

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          onClick={() => onNavigate("TODAY")}
          className="flex items-center gap-1 text-sm bg-blue-100 hover:bg-blue-200 text-gray-900 shadow-none"
        >
          <CalendarIcon size={14} />
          Today
        </Button>

        <Button
          variant="secondary"
          size="icon"
          onClick={() => onNavigate("PREV")}
          className="h-8 w-8 bg-blue-100 hover:bg-blue-200 text-gray-900 shadow-none"
        >
          <ChevronLeft size={16} />
        </Button>

        <Button
          variant="secondary"
          size="icon"
          onClick={() => onNavigate("NEXT")}
          className="h-8 w-8 bg-blue-100 hover:bg-blue-200 text-gray-900 shadow-none"
        >
          <ChevronRight size={16} />
        </Button>

        <div className="relative" ref={datePickerRef}>
          <Button
            variant="ghost"
            onClick={() => setDatePickerOpen((prev) => !prev)}
            className="flex items-center gap-1 text-base font-medium px-2 py-1 h-auto"
          >
            {label}
            <ChevronDown size={16} />
          </Button>

          {datePickerOpen && (
            <div className="absolute mt-2 z-30 bg-white border border-blue-200 rounded-lg shadow-lg">
              <TeamsDatePicker
                selectedDate={date}
                currentView={view}
                onSelectDate={(d) => {
                  setDatePickerOpen(false);
                  onNavigate("DATE", d);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-1 justify-center">
        <div className="relative" ref={viewMenuRef}>
          <Button
            variant="secondary"
            onClick={() => setViewMenuOpen((prev) => !prev)}
            className="flex items-center gap-1 text-sm bg-blue-100 hover:bg-blue-200 text-gray-900 shadow-none"
          >
            <CalendarIcon size={14} />
            {currentViewLabel}
            <ChevronDown size={14} />
          </Button>

          {viewMenuOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white rounded-md border border-gray-200 shadow-md z-20">
              {viewOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant="ghost"
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${view === opt.value
                      ? "text-blue-600 font-medium"
                      : "text-gray-700"
                    }`}
                  onClick={() => handleViewSelect(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {canSelectProviders && (
          <DoctorMultiSelect
            selectedDoctors={selectedDoctors}
            onDoctorSelect={handleSelectDoctors}
            isDropdownOpen={isDropdownOpen}
            setDropdownOpen={setDropdownOpen}
          />
        )}
      </div>

      <div className="relative flex justify-end" ref={addMenuRef}>
        <Button
          disabled={isPastDate() || !canAddAppointment}
          title={
            isPastDate()
              ? "Appointments cannot be added for past dates"
              : !canAddAppointment
              ? "You do not have permission to add appointments"
              : ""
          }
          onClick={() => {
            if (!isPastDate() && canAddAppointment) {
              setShowAddMenu((prev) => !prev);
            }
          }}
          className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-md
            ${isPastDate() || !canAddAppointment
              ? "bg-gray-300 cursor-default text-gray-600"
              : "bg-blue-600 text-white hover:bg-blue-700"
            }
          `}
        >
          + Add <ChevronDown size={14} />
        </Button>
        {showAddMenu && !isPastDate() && canAddAppointment && (
          <div className="absolute right-0 mt-1 w-40 bg-white rounded-md border border-blue-200 shadow-md z-20 text-sm">
            <Button
              variant="ghost"
              className="block w-full text-left px-3 py-2 hover:bg-blue-100"
              onClick={() => {
                setShowAddMenu(false);
                onAddAppointment();
              }}
            >
              Add Appointment
            </Button>

            <Button
              variant="ghost"
              className="block w-full text-left px-3 py-2 hover:bg-blue-100"
              onClick={() => {
                setShowAddMenu(false);
                onAddBulkAppointment();
              }}
            >
              Bulk Upload
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomToolbar
